/**
 * Session Report Store
 * 
 * Manages persisted session reports (read-models) per Doc 07 and Decision D-002.
 * 
 * Reports are append-only artifacts created when a session completes.
 * They capture inputs, outputs, and deltas at the time of completion.
 * 
 * Intent types:
 * - select_attributes: Session for choosing criteria/attributes
 * - converge_values: Session for agreeing on attribute values
 * - select_candidate_pool: Session for choosing which items to rank
 * - rank_priorities: Final ranking session
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, isSupabaseAvailable, generateUUID, createInitialSyncState, type SyncState } from '../../lib/supabaseSync';
import type { PrioritizationSession } from '../../types/prioritization/session';

// ============================================================================
// Types
// ============================================================================

export type SessionReportIntent = 
  | 'select_attributes'
  | 'converge_values'
  | 'select_candidate_pool'
  | 'rank_priorities'
  | 'calibration'
  | 'other';

export interface InputsSnapshot {
  itemIds: string[];
  criteriaIds: string[];
  criteriaWeights: Record<string, number>;
  participants: string[];
  valueSources?: Record<string, 'factual' | 'expert' | 'recommended' | 'unknown'>;
}

export interface OutputsSnapshot {
  ranking?: Array<{ itemId: string; rank: number; score?: number }>;
  selections?: { type: string; selectedIds: string[] };
  acceptedValues?: Array<{ itemId: string; attributeKey: string; value: unknown }>;
  cutLinePosition?: number;
  toolUsed?: string;
  toolConfig?: Record<string, unknown>;
}

export interface DeltasSnapshot {
  newRankings?: string[];
  changedValues?: Array<{ itemId: string; attributeKey: string; from: unknown; to: unknown }>;
  poolAdditions?: string[];
  poolRemovals?: string[];
}

/**
 * Deliberation Record snapshot at report creation (Doc 08 §4; Doc 21 §6.4).
 * Filled server-side by a BEFORE INSERT trigger (migration 726); the report
 * is immutable, so this never reflects annotations/overrides added later.
 */
export interface DeliberationSnapshot {
  recordId?: string | null;
  transcriptStatus: string;
  messageCount: number;
  annotations: Array<{
    itemId: string;
    authorId: string;
    content: string;
    source?: string | null;
    transcriptTimestamp?: string | null;
    createdAt: string;
  }>;
  overrides: Array<{
    itemId: string;
    overriddenBy: string;
    reason?: string | null;
    aggregateResult?: unknown;
    overrideResult?: unknown;
    createdAt: string;
  }>;
  artifacts: Array<{ type: string; title?: string | null; url?: string | null }>;
  capturedAt?: string | null;
}

export interface SessionReport {
  id: string;
  sessionId: string;
  cycleId?: string;
  loopId?: string;
  trackId?: string;
  focusListId?: string;
  intent: SessionReportIntent;
  inputsSnapshot: InputsSnapshot;
  outputsSnapshot: OutputsSnapshot;
  deltasSnapshot?: DeltasSnapshot;
  deliberationSnapshot: DeliberationSnapshot;
  createdBy: string;
  createdAt: Date;
  version: number;
  isFinal: boolean;
  workspaceId?: string;
}

interface SessionReportStoreState {
  reports: SessionReport[];
  isLoading: boolean;
  error: string | null;
  syncState: SyncState;
  
  // Actions
  createReport: (session: PrioritizationSession, actorId: string) => Promise<SessionReport | null>;
  createValueConvergenceReport: (params: {
    cycleId: string;
    itemIds: string[];
    acceptedValues: Array<{ itemId: string; attributeKey: string; value: unknown }>;
    createdBy: string;
  }) => Promise<SessionReport | null>;
  createAttributeSelectionReport: (params: {
    cycleId: string;
    selectedCriteriaIds: string[];
    criteriaWeights?: Record<string, number>;
    createdBy: string;
  }) => Promise<SessionReport | null>;
  createCandidatePoolReport: (params: {
    cycleId: string;
    focusListId: string;
    selectedItemIds: string[];
    selectionReasons?: Record<string, string>;
    createdBy: string;
  }) => Promise<SessionReport | null>;
  createRankingReport: (params: {
    cycleId: string;
    focusListId: string;
    ranking: Array<{ itemId: string; rank: number; score?: number }>;
    toolUsed: string;
    toolConfig?: Record<string, unknown>;
    createdBy: string;
  }) => Promise<SessionReport | null>;
  fetchReportsForSession: (sessionId: string) => Promise<void>;
  fetchReportsForCycle: (cycleId: string) => Promise<void>;
  getReportForSession: (sessionId: string) => SessionReport | undefined;
  getReportsForCycle: (cycleId: string) => SessionReport[];
}

// ============================================================================
// Helper: DB row shape (live schema uses item_pool_id, not focus_list_id)
// ============================================================================

function buildSessionReportInsertRow(report: {
  id: string;
  sessionId: string;
  cycleId?: string | null;
  loopId?: string | null;
  trackId?: string | null;
  itemPoolId?: string | null;
  intent: SessionReportIntent;
  inputsSnapshot: InputsSnapshot;
  outputsSnapshot: OutputsSnapshot;
  deltasSnapshot?: DeltasSnapshot;
  createdBy: string;
  createdAt: Date;
  version: number;
  isFinal: boolean;
  workspaceId?: string | null;
}): Record<string, unknown> {
  const row: Record<string, unknown> = {
    id: report.id,
    session_id: report.sessionId,
    cycle_id: report.cycleId ?? null,
    loop_id: report.loopId ?? null,
    track_id: report.trackId ?? null,
    intent: report.intent,
    inputs_snapshot: report.inputsSnapshot,
    outputs_snapshot: report.outputsSnapshot,
    deltas_snapshot: report.deltasSnapshot ?? {},
    created_by: report.createdBy,
    created_at: report.createdAt.toISOString(),
    generated_at: report.createdAt.toISOString(),
    version: report.version,
    is_final: report.isFinal,
    workspace_id: report.workspaceId ?? null,
  };
  if (report.itemPoolId) {
    row.item_pool_id = report.itemPoolId;
  }
  return row;
}

/**
 * Ranking scores are stored on the session as `Record<itemId, { total?, ...criteria }>`.
 * Reports must persist a numeric score — never the object — or React will crash when
 * rendering (`Objects are not valid as a React child (found: object with keys {total})`).
 */
export function coerceRankingScore(score: unknown): number | undefined {
  if (typeof score === 'number' && Number.isFinite(score)) return score;
  if (score && typeof score === 'object') {
    const record = score as Record<string, unknown>;
    if (typeof record.total === 'number' && Number.isFinite(record.total)) {
      return record.total;
    }
    const sum = Object.entries(record).reduce((acc, [key, val]) => {
      if (key === 'total') return acc;
      return typeof val === 'number' && Number.isFinite(val) ? acc + val : acc;
    }, 0);
    return sum;
  }
  return undefined;
}

function normalizeOutputsSnapshot(raw: unknown): OutputsSnapshot {
  const snapshot = (raw ?? {}) as OutputsSnapshot;
  if (!Array.isArray(snapshot.ranking)) return snapshot;
  return {
    ...snapshot,
    ranking: snapshot.ranking.map((row) => ({
      ...row,
      score: coerceRankingScore(row.score),
    })),
  };
}

function normalizeDeliberationSnapshot(raw: unknown): DeliberationSnapshot {
  const snap = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const annotations = Array.isArray(snap.annotations) ? snap.annotations : [];
  const overrides = Array.isArray(snap.overrides) ? snap.overrides : [];
  const artifacts = Array.isArray(snap.artifacts) ? snap.artifacts : [];

  return {
    recordId: (snap.record_id as string) ?? null,
    transcriptStatus: (snap.transcript_status as string) ?? 'none',
    messageCount: (snap.message_count as number) ?? 0,
    annotations: annotations.map((a) => {
      const row = a as Record<string, unknown>;
      return {
        itemId: row.item_id as string,
        authorId: row.author_id as string,
        content: row.content as string,
        source: (row.source as string) ?? null,
        transcriptTimestamp: (row.transcript_timestamp as string) ?? null,
        createdAt: row.created_at as string,
      };
    }),
    overrides: overrides.map((o) => {
      const row = o as Record<string, unknown>;
      return {
        itemId: row.item_id as string,
        overriddenBy: row.overridden_by as string,
        reason: (row.reason as string) ?? null,
        aggregateResult: row.aggregate_result,
        overrideResult: row.override_result,
        createdAt: row.created_at as string,
      };
    }),
    artifacts: artifacts.map((art) => {
      const row = art as Record<string, unknown>;
      return {
        type: row.type as string,
        title: (row.title as string) ?? null,
        url: (row.url as string) ?? null,
      };
    }),
    capturedAt: (snap.captured_at as string) ?? null,
  };
}

function mapSessionReportRow(row: Record<string, unknown>): SessionReport {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    cycleId: (row.cycle_id as string) ?? undefined,
    loopId: (row.loop_id as string) ?? undefined,
    trackId: (row.track_id as string) ?? undefined,
    focusListId: (row.item_pool_id ?? row.focus_list_id) as string | undefined,
    intent: row.intent as SessionReportIntent,
    inputsSnapshot: row.inputs_snapshot as InputsSnapshot,
    outputsSnapshot: normalizeOutputsSnapshot(row.outputs_snapshot),
    deltasSnapshot: (row.deltas_snapshot as DeltasSnapshot) ?? {},
    deliberationSnapshot: normalizeDeliberationSnapshot(row.deliberation_snapshot),
    createdBy: row.created_by as string,
    createdAt: new Date(row.created_at as string),
    version: (row.version as number) ?? 1,
    isFinal: (row.is_final as boolean) ?? true,
    workspaceId: (row.workspace_id as string) ?? undefined,
  };
}

// ============================================================================
// Helper: Derive intent from session
// ============================================================================

function deriveIntent(session: PrioritizationSession): SessionReportIntent {
  // Check if this is a ranking session
  if (session.rankingOutput && session.rankingOutput.rankedItemIds.length > 0) {
    return 'rank_priorities';
  }
  
  // Check based on session name or other heuristics
  const nameLower = session.name?.toLowerCase() || '';
  
  if (nameLower.includes('criteria') || nameLower.includes('attribute')) {
    return 'select_attributes';
  }
  
  if (nameLower.includes('value') || nameLower.includes('scoring')) {
    return 'converge_values';
  }
  
  if (nameLower.includes('pool') || nameLower.includes('candidate')) {
    return 'select_candidate_pool';
  }
  
  if (nameLower.includes('calibrat')) {
    return 'calibration';
  }
  
  // Default based on whether there's ranking output
  if (session.selectedToolId) {
    return 'rank_priorities';
  }
  
  return 'other';
}

// ============================================================================
// Helper: Build snapshots from session
// ============================================================================

function buildInputsSnapshot(session: PrioritizationSession): InputsSnapshot {
  return {
    itemIds: session.itemIds?.length ? session.itemIds : (session.candidateIds || []),
    criteriaIds: session.criteriaConfig?.criteriaIds || [],
    criteriaWeights: session.criteriaConfig?.weights || {},
    participants: session.participantIds || [],
  };
}

function buildOutputsSnapshot(session: PrioritizationSession): OutputsSnapshot {
  const output: OutputsSnapshot = {};
  
  if (session.rankingOutput) {
    output.ranking = session.rankingOutput.rankedItemIds.map((itemId, index) => ({
      itemId,
      rank: index + 1,
      score: coerceRankingScore(session.rankingOutput?.scores?.[itemId]),
    }));
    output.cutLinePosition = session.cutLinePosition;
    output.toolUsed = session.rankingOutput.toolId;
    output.toolConfig = session.toolConfig;
  }
  
  // Include decisions as selections
  if (session.decisions && session.decisions.length > 0) {
    const inclusions = session.decisions
      .filter(d => d.type === 'candidate_inclusion')
      .map(d => d.relatedItemId)
      .filter((id): id is string => !!id);
    
    if (inclusions.length > 0) {
      output.selections = {
        type: 'candidate_inclusion',
        selectedIds: inclusions,
      };
    }
  }
  
  return output;
}

// ============================================================================
// Store
// ============================================================================

export const useSessionReportStore = create<SessionReportStoreState>()(
  (set, get) => ({
    reports: [],
    isLoading: false,
    error: null,
    syncState: createInitialSyncState(),
    
    // ================================
    // Create Report
    // ================================
    
    createReport: async (session, actorId) => {
      if (!isSupabaseAvailable()) {
        console.warn('[SessionReportStore] Supabase not available');
        return null;
      }
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;
      
      // Check if a final report already exists for this session
      const existing = get().reports.find(r => r.sessionId === session.id && r.isFinal);
      if (existing) {
        console.log('[SessionReportStore] Final report already exists for session:', session.id);
        return existing;
      }
      
      const report: SessionReport = {
        id: generateUUID(),
        sessionId: session.id,
        cycleId: session.cycleId,
        loopId: undefined, // Would need to be passed in if available
        trackId: session.trackId ?? undefined,
        focusListId: session.resultingItemPoolId,
        intent: deriveIntent(session),
        inputsSnapshot: buildInputsSnapshot(session),
        outputsSnapshot: buildOutputsSnapshot(session),
        deltasSnapshot: {},
        createdBy: actorId,
        createdAt: new Date(),
        version: 1,
        isFinal: true,
        workspaceId: session.workspaceId,
      };
      
      try {
        const { error } = await supabase
          .from('session_reports')
          .insert(
            buildSessionReportInsertRow({
              id: report.id,
              sessionId: report.sessionId,
              cycleId: report.cycleId ?? null,
              loopId: report.loopId ?? null,
              trackId: report.trackId ?? null,
              itemPoolId: report.focusListId ?? null,
              intent: report.intent,
              inputsSnapshot: report.inputsSnapshot,
              outputsSnapshot: report.outputsSnapshot,
              deltasSnapshot: report.deltasSnapshot,
              createdBy: report.createdBy,
              createdAt: report.createdAt,
              version: report.version,
              isFinal: report.isFinal,
              workspaceId: report.workspaceId ?? null,
            })
          )
          .select()
          .single();
        
        if (error) {
          console.error('[SessionReportStore] Failed to create report:', error);
          set({ error: error.message });
          return null;
        }
        
        // Add to local state
        set(state => ({
          reports: [...state.reports, report],
          error: null,
        }));
        
        console.log('[SessionReportStore] Created session report:', report.id, 'for session:', session.id, 'intent:', report.intent);
        return report;
        
      } catch (err) {
        console.error('[SessionReportStore] Exception creating report:', err);
        set({ error: String(err) });
        return null;
      }
    },

    /**
     * Create a value convergence report directly (without a session)
     * Per Doc 07: Reports are durable artifacts created when work completes
     */
    createValueConvergenceReport: async (params) => {
      if (!isSupabaseAvailable()) {
        console.warn('[SessionReportStore] Supabase not available');
        return null;
      }
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;
      
      const now = new Date();
      const report: SessionReport = {
        id: generateUUID(),
        sessionId: `value-convergence-${now.getTime()}`, // Synthetic session ID
        cycleId: params.cycleId,
        loopId: undefined,
        focusListId: undefined,
        intent: 'converge_values',
        inputsSnapshot: {
          itemIds: params.itemIds,
          criteriaIds: [],
          criteriaWeights: {},
          participants: [params.createdBy],
        },
        outputsSnapshot: {
          acceptedValues: params.acceptedValues,
        },
        deltasSnapshot: {
          changedValues: params.acceptedValues.map(av => ({
            itemId: av.itemId,
            attributeKey: av.attributeKey,
            from: undefined,
            to: av.value,
          })),
        },
        createdBy: params.createdBy,
        createdAt: now,
        version: 1,
        isFinal: true,
      };
      
      try {
        const { error } = await supabase
          .from('session_reports')
          .insert(buildSessionReportInsertRow({
            id: report.id,
            sessionId: report.sessionId,
            cycleId: report.cycleId ?? null,
            loopId: report.loopId ?? null,
            trackId: report.trackId ?? null,
            itemPoolId: report.focusListId ?? null,
            intent: report.intent,
            inputsSnapshot: report.inputsSnapshot,
            outputsSnapshot: report.outputsSnapshot,
            deltasSnapshot: report.deltasSnapshot,
            createdBy: report.createdBy,
            createdAt: report.createdAt,
            version: report.version,
            isFinal: report.isFinal,
            workspaceId: report.workspaceId ?? null,
          }))
          .select()
          .single();
        
        if (error) {
          console.error('[SessionReportStore] Failed to create value convergence report:', error);
          set({ error: error.message });
          return null;
        }
        
        // Add to local state
        set(state => ({
          reports: [...state.reports, report],
          error: null,
        }));
        
        // Emit governance event (non-blocking)
        supabase.from('governance_events').insert({
          id: generateUUID(),
          cycle_id: params.cycleId,
          event_type: 'value_convergence_completed',
          actor_id: params.createdBy,
          occurred_at: now.toISOString(),
          note: JSON.stringify({ 
            reportId: report.id,
            itemCount: params.itemIds.length,
            valueCount: params.acceptedValues.length,
          }),
        }).then(({ error: govError }) => {
          if (govError) {
            console.warn('[SessionReportStore] Governance event failed (non-blocking):', govError.message);
          }
        });
        
        console.log('[SessionReportStore] Created value convergence report:', report.id, 'for cycle:', params.cycleId);
        return report;
        
      } catch (err) {
        console.error('[SessionReportStore] Exception creating value convergence report:', err);
        set({ error: String(err) });
        return null;
      }
    },

    /**
     * Create an attribute selection report (criteria selection)
     * Per Doc 07: Reports capture the output of selecting which attributes become criteria
     */
    createAttributeSelectionReport: async (params) => {
      if (!isSupabaseAvailable()) {
        console.warn('[SessionReportStore] Supabase not available');
        return null;
      }
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;
      
      const now = new Date();
      const report: SessionReport = {
        id: generateUUID(),
        sessionId: `attribute-selection-${now.getTime()}`,
        cycleId: params.cycleId,
        loopId: undefined,
        focusListId: undefined,
        intent: 'select_attributes',
        inputsSnapshot: {
          itemIds: [],
          criteriaIds: params.selectedCriteriaIds,
          criteriaWeights: params.criteriaWeights || {},
          participants: [params.createdBy],
        },
        outputsSnapshot: {
          selections: {
            type: 'criteria_selection',
            selectedIds: params.selectedCriteriaIds,
          },
        },
        deltasSnapshot: {},
        createdBy: params.createdBy,
        createdAt: now,
        version: 1,
        isFinal: true,
      };
      
      try {
        const { error } = await supabase
          .from('session_reports')
          .insert(buildSessionReportInsertRow({
            id: report.id,
            sessionId: report.sessionId,
            cycleId: report.cycleId ?? null,
            loopId: report.loopId ?? null,
            trackId: report.trackId ?? null,
            itemPoolId: report.focusListId ?? null,
            intent: report.intent,
            inputsSnapshot: report.inputsSnapshot,
            outputsSnapshot: report.outputsSnapshot,
            deltasSnapshot: report.deltasSnapshot,
            createdBy: report.createdBy,
            createdAt: report.createdAt,
            version: report.version,
            isFinal: report.isFinal,
            workspaceId: report.workspaceId ?? null,
          }))
          .select()
          .single();
        
        if (error) {
          console.error('[SessionReportStore] Failed to create attribute selection report:', error);
          set({ error: error.message });
          return null;
        }
        
        set(state => ({
          reports: [...state.reports, report],
          error: null,
        }));
        
        // Emit governance event (non-blocking)
        supabase.from('governance_events').insert({
          id: generateUUID(),
          cycle_id: params.cycleId,
          event_type: 'criteria_changed',
          actor_id: params.createdBy,
          occurred_at: now.toISOString(),
          note: JSON.stringify({ 
            reportId: report.id,
            criteriaCount: params.selectedCriteriaIds.length,
            criteriaIds: params.selectedCriteriaIds,
          }),
        }).then(({ error: govError }) => {
          if (govError) {
            console.warn('[SessionReportStore] Governance event failed (non-blocking):', govError.message);
          }
        });
        
        console.log('[SessionReportStore] Created attribute selection report:', report.id, 'for cycle:', params.cycleId);
        return report;
        
      } catch (err) {
        console.error('[SessionReportStore] Exception creating attribute selection report:', err);
        set({ error: String(err) });
        return null;
      }
    },

    /**
     * Create a candidate pool selection report
     * Per Doc 07: Reports capture the output of selecting which items to rank
     */
    createCandidatePoolReport: async (params) => {
      if (!isSupabaseAvailable()) {
        console.warn('[SessionReportStore] Supabase not available');
        return null;
      }
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;
      
      const now = new Date();
      const report: SessionReport = {
        id: generateUUID(),
        sessionId: `candidate-pool-${now.getTime()}`,
        cycleId: params.cycleId,
        loopId: undefined,
        focusListId: params.focusListId,
        intent: 'select_candidate_pool',
        inputsSnapshot: {
          itemIds: params.selectedItemIds,
          criteriaIds: [],
          criteriaWeights: {},
          participants: [params.createdBy],
        },
        outputsSnapshot: {
          selections: {
            type: 'candidate_pool',
            selectedIds: params.selectedItemIds,
          },
        },
        deltasSnapshot: {
          poolAdditions: params.selectedItemIds,
        },
        createdBy: params.createdBy,
        createdAt: now,
        version: 1,
        isFinal: true,
      };
      
      try {
        const { error } = await supabase
          .from('session_reports')
          .insert(buildSessionReportInsertRow({
            id: report.id,
            sessionId: report.sessionId,
            cycleId: report.cycleId ?? null,
            loopId: report.loopId ?? null,
            trackId: report.trackId ?? null,
            itemPoolId: report.focusListId ?? null,
            intent: report.intent,
            inputsSnapshot: report.inputsSnapshot,
            outputsSnapshot: report.outputsSnapshot,
            deltasSnapshot: report.deltasSnapshot,
            createdBy: report.createdBy,
            createdAt: report.createdAt,
            version: report.version,
            isFinal: report.isFinal,
            workspaceId: report.workspaceId ?? null,
          }))
          .select()
          .single();
        
        if (error) {
          console.error('[SessionReportStore] Failed to create Item Pool report:', error);
          set({ error: error.message });
          return null;
        }
        
        set(state => ({
          reports: [...state.reports, report],
          error: null,
        }));
        
        // Emit governance event (non-blocking)
        supabase.from('governance_events').insert({
          id: generateUUID(),
          cycle_id: params.cycleId,
          event_type: 'pool_modified',
          actor_id: params.createdBy,
          occurred_at: now.toISOString(),
          note: JSON.stringify({ 
            reportId: report.id,
            focusListId: params.focusListId,
            itemCount: params.selectedItemIds.length,
            selectionReasons: params.selectionReasons,
          }),
        }).then(({ error: govError }) => {
          if (govError) {
            console.warn('[SessionReportStore] Governance event failed (non-blocking):', govError.message);
          }
        });
        
        console.log('[SessionReportStore] Created Item Pool report:', report.id, 'for cycle:', params.cycleId);
        return report;
        
      } catch (err) {
        console.error('[SessionReportStore] Exception creating Item Pool report:', err);
        set({ error: String(err) });
        return null;
      }
    },

    /**
     * Create a ranking report
     * Per Doc 07: Reports capture the output of a ranking session with tool inputs/outputs
     */
    createRankingReport: async (params) => {
      if (!isSupabaseAvailable()) {
        console.warn('[SessionReportStore] Supabase not available');
        return null;
      }
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;
      
      const now = new Date();
      const report: SessionReport = {
        id: generateUUID(),
        sessionId: `ranking-${now.getTime()}`,
        cycleId: params.cycleId,
        loopId: undefined,
        focusListId: params.focusListId,
        intent: 'rank_priorities',
        inputsSnapshot: {
          itemIds: params.ranking.map(r => r.itemId),
          criteriaIds: [],
          criteriaWeights: {},
          participants: [params.createdBy],
        },
        outputsSnapshot: {
          ranking: params.ranking,
          toolUsed: params.toolUsed,
          toolConfig: params.toolConfig,
        },
        deltasSnapshot: {
          newRankings: params.ranking.map(r => r.itemId),
        },
        createdBy: params.createdBy,
        createdAt: now,
        version: 1,
        isFinal: true,
      };
      
      try {
        const { error } = await supabase
          .from('session_reports')
          .insert(buildSessionReportInsertRow({
            id: report.id,
            sessionId: report.sessionId,
            cycleId: report.cycleId ?? null,
            loopId: report.loopId ?? null,
            trackId: report.trackId ?? null,
            itemPoolId: report.focusListId ?? null,
            intent: report.intent,
            inputsSnapshot: report.inputsSnapshot,
            outputsSnapshot: report.outputsSnapshot,
            deltasSnapshot: report.deltasSnapshot,
            createdBy: report.createdBy,
            createdAt: report.createdAt,
            version: report.version,
            isFinal: report.isFinal,
            workspaceId: report.workspaceId ?? null,
          }))
          .select()
          .single();
        
        if (error) {
          console.error('[SessionReportStore] Failed to create ranking report:', error);
          set({ error: error.message });
          return null;
        }
        
        set(state => ({
          reports: [...state.reports, report],
          error: null,
        }));
        
        console.log('[SessionReportStore] Created ranking report:', report.id, 'for cycle:', params.cycleId);
        return report;
        
      } catch (err) {
        console.error('[SessionReportStore] Exception creating ranking report:', err);
        set({ error: String(err) });
        return null;
      }
    },
    
    // ================================
    // Fetch Reports
    // ================================
    
    fetchReportsForSession: async (sessionId) => {
      if (!isSupabaseAvailable()) return;
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;
      
      set({ isLoading: true, error: null });
      
      try {
        const { data, error } = await supabase
          .from('session_reports')
          .select('*')
          .eq('session_id', sessionId)
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('[SessionReportStore] Failed to fetch reports:', error);
          set({ isLoading: false, error: error.message });
          return;
        }
        
        if (data) {
          const reports: SessionReport[] = data.map((row: Record<string, unknown>) =>
            mapSessionReportRow(row)
          );
          
          // Merge with existing reports (avoid duplicates)
          set(state => {
            const existingIds = new Set(reports.map(r => r.id));
            const otherReports = state.reports.filter(r => !existingIds.has(r.id) && r.sessionId !== sessionId);
            return {
              reports: [...otherReports, ...reports],
              isLoading: false,
            };
          });
          
          console.log('[SessionReportStore] Loaded', reports.length, 'reports for session:', sessionId);
        }
      } catch (err) {
        console.error('[SessionReportStore] Exception fetching reports:', err);
        set({ isLoading: false, error: String(err) });
      }
    },
    
    fetchReportsForCycle: async (cycleId) => {
      if (!isSupabaseAvailable()) return;
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;
      
      set({ isLoading: true, error: null });
      
      try {
        const { data, error } = await supabase
          .from('session_reports')
          .select('*')
          .eq('cycle_id', cycleId)
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('[SessionReportStore] Failed to fetch cycle reports:', error);
          set({ isLoading: false, error: error.message });
          return;
        }
        
        if (data) {
          const reports: SessionReport[] = data.map((row: Record<string, unknown>) =>
            mapSessionReportRow(row)
          );
          
          // Merge with existing reports
          set(state => {
            const existingIds = new Set(reports.map(r => r.id));
            const otherReports = state.reports.filter(r => !existingIds.has(r.id) && r.cycleId !== cycleId);
            return {
              reports: [...otherReports, ...reports],
              isLoading: false,
            };
          });
          
          console.log('[SessionReportStore] Loaded', reports.length, 'reports for cycle:', cycleId);
        }
      } catch (err) {
        console.error('[SessionReportStore] Exception fetching cycle reports:', err);
        set({ isLoading: false, error: String(err) });
      }
    },
    
    // ================================
    // Selectors
    // ================================
    
    getReportForSession: (sessionId) => {
      return get().reports.find(r => r.sessionId === sessionId && r.isFinal);
    },
    
    getReportsForCycle: (cycleId) => {
      return get().reports
        .filter(r => r.cycleId === cycleId)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    },
  })
);

export default useSessionReportStore;
